import React, { useState, useEffect } from 'react';
import ToggleSwitch from './ToggleSwitch';
import { PERSONALITIES } from '../../utils/prompts';
import { PersonalityOption } from '../../types';
import ConversationHistory from '../History/ConversationHistory';

interface TallySettingsProps {
  userEmail?: string;
  showUserTranscript: boolean;
  showAITranscript: boolean;
  aiVoice: string;
  personalityType: PersonalityOption;
  feedbackPrompt: string;
  mcqPrompt: string;
  onToggleUserTranscript: () => void;
  onToggleAITranscript: () => void;
  onChangeVoice: (voice: string) => void;
  onChangePersona: (persona: PersonalityOption) => void;
  onChangeFeedbackPrompt: (prompt: string) => void;
  onChangeMCQPrompt: (prompt: string) => void;
  onResumeConversation?: (sessionData: any) => void;
  historyOnly?: boolean;
}

// Define common variables used in prompts
const PROMPT_VARIABLES = [
  {
    name: 'userName',
    description: 'The student\'s name for personalization',
    example: 'John',
    defaultValue: 'there',
    usage: '{{userName}}'
  },
  {
    name: 'userLevel',
    description: 'English proficiency level',
    example: 'intermediate',
    defaultValue: 'intermediate',
    options: ['beginner', 'intermediate', 'advanced'],
    usage: '{{userLevel}}'
  },
  {
    name: 'industry',
    description: 'User\'s industry for context-relevant examples',
    example: 'technology',
    defaultValue: 'business',
    usage: '{{industry}}'
  },
  {
    name: 'conversationTopic',
    description: 'The specific topic or scenario chosen',
    example: 'giving feedback to a direct report',
    defaultValue: '',
    usage: '{{conversationTopic}}'
  },
  {
    name: 'skillCategory',
    description: 'Skills DB category (when launched from Skills DB)',
    example: 'leadership',
    defaultValue: '',
    usage: '{{skillCategory}}'
  },
  {
    name: 'skillName',
    description: 'Specific skill being practiced',
    example: 'active listening',
    defaultValue: '',
    usage: '{{skillName}}'
  }
];

const SettingsPanelEnhanced: React.FC<TallySettingsProps> = ({
  userEmail,
  showUserTranscript,
  showAITranscript,
  aiVoice,
  personalityType,
  feedbackPrompt,
  mcqPrompt,
  onToggleUserTranscript,
  onToggleAITranscript,
  onChangeVoice,
  onChangePersona,
  onChangeFeedbackPrompt,
  onChangeMCQPrompt,
  onResumeConversation,
  historyOnly
}) => {
  const voices = ['marin', 'alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'];
  const [activeTab, setActiveTab] = useState<'settings' | 'history'>(historyOnly ? 'history' : 'settings');


  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tabs */}
      {!historyOnly && (
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-600 border-transparent hover:text-gray-800'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-600 border-transparent hover:text-gray-800'
            }`}
          >
            History
          </button>
        </div>
      )}
      
      {(!historyOnly && activeTab === 'settings') ? (
        <div className="p-6">
          <h2 className="text-lg font-medium mb-4">Conversation Settings</h2>
          <div className="space-y-4">
        <ToggleSwitch label="Show Your Transcripts" isChecked={showUserTranscript} onChange={onToggleUserTranscript} />
        <ToggleSwitch label="Show Tally's Transcripts" isChecked={showAITranscript} onChange={onToggleAITranscript} />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">AI Voice</label>
          <select
            value={aiVoice}
            onChange={e => onChangeVoice(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          >
            {voices.map(v => (
              <option key={v} value={v}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Personality Type</label>
          <select
            value={personalityType}
            onChange={e => onChangePersona(e.target.value as PersonalityOption)}
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          >
            {Object.entries(PERSONALITIES).map(([key, { name, description }]) => (
              <option key={key} value={key}>
                {name} - {description}
              </option>
            ))}
          </select>
        </div>

        {/* Prompt configuration removed: prompt is now fully backend-controlled */}

        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-lg font-medium mb-2">Feedback Settings</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Feedback Prompt</label>
            <textarea
              value={feedbackPrompt}
              onChange={e => onChangeFeedbackPrompt(e.target.value)}
              rows={12}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 font-sans text-sm"
              placeholder="Instructions for generating English feedback..."
            />
            <p className="mt-1 text-xs text-gray-500">
              This prompt controls how English feedback is generated. Make sure to preserve the markdown formatting instructions.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-lg font-medium mb-2">Practice Exercise Settings</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MCQ Generation Prompt</label>
            <textarea
              value={mcqPrompt}
              onChange={e => onChangeMCQPrompt(e.target.value)}
              rows={12}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 font-sans text-sm"
              placeholder="Instructions for generating practice exercises..."
            />
            <p className="mt-1 text-xs text-gray-500">
              This prompt controls how practice exercises are generated based on the feedback.
            </p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded border text-sm text-gray-600">
          <p>
            <strong>Note:</strong> These settings determine how Tally speaks and behaves.
            Changes will take effect when you start a new conversation.
          </p>
          </div>
        </div>
        </div>
      ) : (
        <ConversationHistory userEmail={userEmail || 'anonymous'} onResumeConversation={onResumeConversation} />
      )}
    </div>
  );
};

export default SettingsPanelEnhanced;